<template>
  <div class="p-6">
    <div class="w-full bg-white rounded-xl shadow-md border border-gray-200">

      <!-- Header -->
      <div class="px-6 py-4 border-b border-gray-100">
        <h2 class="text-lg font-semibold text-gray-800">Job Openings</h2>
        <p class="text-sm text-gray-500">
          Manage and publish open positions to inform candidates that your company is actively hiring.
        </p>
      </div>

      <!-- Search & Add Job -->
      <div
        class="px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div class="flex items-center gap-2">
          <h2 class="text-sm font-semibold text-gray-700">Job List</h2>
          <span class="text-xs text-gray-400">({{ jobs.length }} listings)</span>
        </div>

        <div class="flex items-center gap-3">
          <div class="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 w-64">
            <svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z" />
            </svg>
            <input v-model="searchQuery" @input="handleSearch" type="text"
              placeholder="Search title, location, type, status..."
              class="bg-transparent text-sm text-gray-700 outline-none w-full placeholder-gray-400" />
          </div>

          <button @click="openAddModal"
            class="px-4 py-2 text-sm rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors flex items-center gap-1.5">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Post a Job
          </button>
        </div>
      </div>

      <!-- Jobs Table -->
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 text-gray-600 uppercase text-xs">
            <tr>
              <th class="px-4 py-3 text-left">#</th>
              <th class="px-4 py-3 text-left">Job Title</th>
              <th class="px-4 py-3 text-left">Description</th> <!-- 👈 NEW -->
              <th class="px-4 py-3 text-left">Location</th>
              <th class="px-4 py-3 text-left">Type</th>
              <th class="px-4 py-3 text-left">Status</th>
              <th class="px-4 py-3 text-left">Date Posted</th>
              <th class="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            <!-- Loading -->
            <tr v-if="isLoading">
              <td colspan="8" class="text-center py-8">
                <svg class="animate-spin h-5 w-5 text-orange-400 mx-auto" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              </td>
            </tr>

            <!-- Empty -->
            <tr v-else-if="jobs.length === 0">
              <td colspan="8" class="text-center text-gray-400 py-10">
                No job openings found.
              </td>
            </tr>

            <!-- Data -->
            <tr v-for="(job, index) in jobs" :key="job.id" class="border-t border-gray-100 hover:bg-gray-50 transition">
              <td class="px-4 py-3 text-gray-500">
                {{ index + 1 + (currentPage - 1) * perPage }}
              </td>

              <!-- Title -->
              <td class="px-4 py-3">
                <p class="font-medium text-gray-800">{{ job.title }}</p>
                <span class="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded mt-1 inline-block">
                  {{ job.department }}
                </span>
              </td>

              <!-- 🔥 Description Column -->
              <td class="px-4 py-3 text-gray-600 max-w-xs">
                <p class="text-xs line-clamp-2">
                  {{
                    job.description && job.description.trim()
                      ? job.description
                      : 'No description available.'
                  }}
                </p>
              </td>

              <!-- Location -->
              <td class="px-4 py-3 text-gray-600">{{ job.location }}</td>

              <!-- Type -->
              <td class="px-4 py-3">
                <span :class="typeBadgeClass(job.type)" class="text-xs font-semibold px-2.5 py-1 rounded-full">
                  {{ job.type }}
                </span>
              </td>

              <!-- Status -->
              <td class="px-4 py-3">
                <span :class="statusBadgeClass(job.status)" class="text-xs font-semibold px-2.5 py-1 rounded-full">
                  {{ job.status }}
                </span>
              </td>

              <!-- Date -->
              <td class="px-4 py-3 text-gray-500 text-xs">
                {{ job.postedDate }}
              </td>

              <!-- Actions -->
              <td class="px-4 py-3 text-center flex justify-center gap-2">
                <button @click="editJob(job)" class="text-indigo-500 hover:text-indigo-700 font-medium text-sm">
                  Edit
                </button>

                <button @click="toggleStatus(job)" class="text-blue-500 hover:text-blue-700 font-medium text-sm">
                  {{ job.status === 'Active' ? 'Disable' : 'Enable' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <HeroPagination :currentPage="currentPage" :totalPages="totalPages"
        @page-changed="page => { currentPage = page; fetchJobs(); }" />
    </div>

    <!-- Add Job Modal -->
    <AddJobModal v-model="showModal" :job="selectedJob" @refresh-jobs="fetchJobs" />
  </div>

</template>

<script>
import { ref, onMounted } from "vue";
import AddJobModal from "@/components/sections/careers-recruitment/Modals/JobOpeningModal.vue";
import HeroPagination from "@/components/common/HeroPagination.vue";
import { getAllJobs, updateJob } from "@/api/jobs";

export default {
  components: { AddJobModal, HeroPagination },

  setup() {
    const showModal = ref(false);
    const searchQuery = ref("");
    const isLoading = ref(false);
    const currentPage = ref(1);
    const perPage = 7;
    const jobs = ref([]);
    const selectedJob = ref(null);
    const totalPages = ref(1);

    const fetchJobs = async () => {
      try {
        isLoading.value = true;
        const { data } = await getAllJobs({ page: currentPage.value, limit: perPage, search: searchQuery.value });
        jobs.value = data.data.map(job => ({
          ...job,
          postedDate: job.posted_at ? new Date(job.posted_at).toLocaleDateString() : ""
        }));
        totalPages.value = data.pagination.totalPages;
      } catch (err) {
        console.error(err);
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(fetchJobs);

    const typeBadgeClass = type => ({
      "Full-time": "bg-emerald-50 text-emerald-700",
      "Part-time": "bg-yellow-50 text-yellow-700",
      Contract: "bg-orange-50 text-orange-700",
      Internship: "bg-purple-50 text-purple-700",
      Remote: "bg-blue-50 text-blue-700"
    }[type] || "bg-slate-100 text-slate-600");

    const statusBadgeClass = status => ({
      Active: "bg-emerald-50 text-emerald-700",
      Inactive: "bg-red-50 text-red-700"
    }[status] || "bg-slate-100 text-slate-600");

    const openAddModal = () => { selectedJob.value = null; showModal.value = true; };
    const editJob = job => { selectedJob.value = { ...job }; showModal.value = true; };

    // Direct toggle without confirmation modal
    const toggleStatus = async (job) => {
      const newStatus = job.status === "Active" ? "Inactive" : "Active";
      try {
        await updateJob(job.id, { status: newStatus });
        await fetchJobs();
      } catch (err) {
        console.error(err);
      }
    };

    const handleSearch = () => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        currentPage.value = 1;
        fetchJobs();
      }, 400);
    };

    let searchTimeout = null;

    return {
      showModal,
      searchQuery,
      jobs,
      currentPage,
      totalPages,
      perPage,
      isLoading,
      selectedJob,
      editJob,
      fetchJobs,
      toggleStatus,
      handleSearch,
      openAddModal,
      typeBadgeClass,
      statusBadgeClass,
    };
  }
};
</script>